import { format } from 'date-fns'
import { Search, ShieldOff } from 'lucide-react'
import { useState } from 'react'
import { useRemoveSuppression, useSuppressions } from '@/api/hooks/use-suppressions'
import { DataTable } from '@/components/ui/data-table'
import { StatusBadge } from '@/components/ui/status-badge'
import { AddSuppressionModal } from '@/pages/suppressions-add-modal'

export function SuppressionsPage(): React.JSX.Element {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [showAdd, setShowAdd] = useState(false)
  const [removeTarget, setRemoveTarget] = useState<{ id: string; email: string } | null>(null)

  const { data, isLoading } = useSuppressions({ search: search || undefined, page })
  const removeSuppression = useRemoveSuppression()

  const suppressions = data?.data ?? []
  const pagination = data?.pagination

  function handleRemove(): void {
    if (!removeTarget) return
    removeSuppression.mutate(removeTarget.id, { onSuccess: () => setRemoveTarget(null) })
  }

  if (isLoading) {
    return <div className="py-12 text-center text-text-tertiary">Loading suppressions...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-text-primary">Suppressions</h1>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
        >
          + Add Address
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          placeholder="Search by email address..."
          className="w-full rounded-lg border border-surface-3 py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none"
        />
      </div>

      {suppressions.length === 0 ? (
        <EmptySuppressions hasSearch={search.length > 0} onAddClick={() => setShowAdd(true)} />
      ) : (
        <DataTable
          columns={[
            {
              key: 'email_address',
              label: 'Email',
              sortable: true,
              render: (row) => <span className="font-medium">{row.email_address}</span>,
            },
            {
              key: 'reason',
              label: 'Reason',
              render: (row) => <StatusBadge status={row.reason} />,
            },
            {
              key: 'created_at',
              label: 'Added',
              sortable: true,
              render: (row) => (
                <span className="text-text-secondary">
                  {format(new Date(row.created_at), 'MMM d, yyyy')}
                </span>
              ),
            },
            {
              key: 'actions',
              label: '',
              render: (row) => (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setRemoveTarget({ id: row.id, email: row.email_address })
                  }}
                  className="rounded-md px-3 py-1 text-sm text-danger hover:bg-red-50"
                >
                  Remove
                </button>
              ),
            },
          ]}
          data={suppressions}
          pagination={
            pagination
              ? {
                  page: pagination.page,
                  perPage: pagination.per_page,
                  total: pagination.total,
                }
              : undefined
          }
          onPageChange={setPage}
        />
      )}

      {showAdd && <AddSuppressionModal onClose={() => setShowAdd(false)} />}

      {removeTarget && (
        <RemoveConfirmation
          email={removeTarget.email}
          isPending={removeSuppression.isPending}
          onConfirm={handleRemove}
          onCancel={() => setRemoveTarget(null)}
        />
      )}
    </div>
  )
}

function EmptySuppressions({
  hasSearch,
  onAddClick,
}: {
  hasSearch: boolean
  onAddClick: () => void
}): React.JSX.Element {
  if (hasSearch) {
    return (
      <p className="py-12 text-center text-text-tertiary">No suppressions match your search.</p>
    )
  }
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <ShieldOff className="mb-4 h-12 w-12 text-text-tertiary" />
      <h3 className="text-lg font-medium text-text-primary">No suppressed addresses</h3>
      <p className="mt-1 max-w-sm text-sm text-text-secondary">
        Hard bounces and complaints are automatically added here. You can also add addresses
        manually.
      </p>
      <button
        type="button"
        onClick={onAddClick}
        className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
      >
        Add an address
      </button>
    </div>
  )
}

function RemoveConfirmation({
  email,
  isPending,
  onConfirm,
  onCancel,
}: {
  email: string
  isPending: boolean
  onConfirm: () => void
  onCancel: () => void
}): React.JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl bg-surface-0 p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-text-primary">Remove Suppression</h3>
        <p className="mt-2 text-sm text-text-secondary">
          Are you sure? Removing {email} from the suppression list will allow emails to be sent to
          this address again.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-surface-3 px-4 py-2 text-sm hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="rounded-lg bg-danger px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50"
          >
            {isPending ? 'Removing...' : 'Remove'}
          </button>
        </div>
      </div>
    </div>
  )
}
